import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Download, FileArchive, Users, ClipboardList, CreditCard, GraduationCap, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Onglet "Sauvegardes" — exports CSV ciblés + sauvegarde complète ZIP.
 *
 * Bonne pratique : à télécharger régulièrement (idéalement une fois par mois minimum)
 * et à conserver sur 2 supports différents (cloud personnel + clé USB par exemple).
 */
export default function BackupsTab({ selectedYearId }) {
  return (
    <div className="space-y-6" data-testid="backups-tab">
      <div className="bg-[#FFF5E5] border border-[#C86444]/30 rounded-2xl p-4 flex gap-3">
        <ShieldCheck className="w-5 h-5 text-[#C86444] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">Sauvegardes régulières recommandées</h3>
          <p className="text-xs text-[#5c6374] mt-1 leading-relaxed">
            Téléchargez régulièrement vos données pour prévenir toute perte. Idéalement <strong>une sauvegarde complète chaque mois</strong>
            et avant tout changement majeur (clôture d'année, modification massive). Conservez les fichiers sur 2 supports différents
            (cloud personnel + clé USB ou disque externe).
          </p>
        </div>
      </div>

      {/* Sauvegarde complète */}
      <BackupCard
        icon={FileArchive}
        title="Sauvegarde complète (ZIP)"
        description="Archive contenant TOUTES les données de l'application : utilisateurs, demandes, groupes, cours, présences, paiements, événements, messages, devoirs, heures, années scolaires. Format JSON par collection. Pratique pour une restauration future ou un audit."
        cta="Télécharger l'archive ZIP"
        path="/admin/backup/full"
        filename="sauvegarde_complete.zip"
        mime="application/zip"
        testId="backup-full"
        primary
      />

      <div>
        <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547] mb-3">Exports CSV ciblés</h3>
        <p className="text-xs text-[#5c6374] mb-4">Fichiers Excel/LibreOffice prêts à l'emploi pour consultation ou import comptable.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <BackupCard
            icon={Users}
            title="Tous les utilisateurs"
            description="Liste complète des comptes : parents, enseignants, administrateurs. Les mots de passe ne sont jamais exportés."
            cta="Télécharger CSV"
            path="/admin/users/export.csv"
            filename="utilisateurs.csv"
            mime="text/csv"
            testId="backup-users"
          />

          <BackupCard
            icon={ClipboardList}
            title="Présences de l'année"
            description="Toutes les présences enregistrées (présent / absent / retard / excusé) avec date, élève, groupe, matière et enseignant."
            cta="Télécharger CSV"
            path="/admin/attendance/export.csv"
            params={selectedYearId ? { year_id: selectedYearId } : {}}
            filename="presences.csv"
            mime="text/csv"
            testId="backup-attendance"
          />

          <BackupCard
            icon={GraduationCap}
            title="Demandes d'inscription"
            description="Toutes les demandes (en attente, validées, rejetées) avec coordonnées des parents et enfants."
            cta="Télécharger CSV"
            path="/enrollment-requests/export.csv"
            filename="inscriptions.csv"
            mime="text/csv"
            testId="backup-requests"
          />

          <BackupCard
            icon={CreditCard}
            title="Paiements par famille"
            description="Suivi des cotisations annuelles : montant dû, payé, mode (chèques / espèces), notes."
            cta="Télécharger CSV"
            path="/admin/family-payments/export.csv"
            params={selectedYearId ? { year_id: selectedYearId } : {}}
            filename="paiements.csv"
            mime="text/csv"
            testId="backup-payments"
          />
        </div>
      </div>

      <div>
        <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547] mb-3">Remises à zéro</h3>
        <p className="text-xs text-[#5c6374] mb-4">Utile en début d'année scolaire ou après une saisie erronée. <strong>Actions irréversibles</strong> — pensez à faire une sauvegarde avant.</p>
        <div className="space-y-4">
          <ResetSection
            title="Remise à zéro du suivi des absences"
            description="Efface les saisies de présences (présent / absent / retard / excusé) de l'année active ou sur une plage de dates."
            endpoint="/admin/attendance/reset"
            itemLabel="saisie(s) de présence"
            cta="Effacer les présences"
            confirmLabel="les présences"
            testId="attendance-reset"
          />
          <ResetSection
            title="Remise à zéro des devoirs"
            description="Efface les devoirs donnés par les enseignants ainsi que leurs suivis de complétion. La plage de dates s'applique à la date d'échéance du devoir."
            endpoint="/admin/homeworks/reset"
            itemLabel="devoir(s)"
            cta="Effacer les devoirs"
            confirmLabel="les devoirs"
            testId="homeworks-reset"
          />
          <ResetSection
            title="Remise à zéro des dédommagements"
            description="Efface les saisies d'heures des enseignants (en attente, validées et rejetées). La plage de dates s'applique à la date de la saisie."
            endpoint="/admin/work-hours/reset"
            itemLabel="saisie(s) d'heures"
            cta="Effacer les saisies"
            confirmLabel="les saisies de dédommagement"
            testId="workhours-reset"
          />
        </div>
      </div>

      <div className="bg-[#FBE5E4] border border-[#B0413E]/30 rounded-2xl p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-[#B0413E] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">Confidentialité des données (RGPD)</h3>
          <p className="text-xs text-[#5c6374] mt-1 leading-relaxed">
            Les sauvegardes contiennent des <strong>données personnelles</strong> (noms, emails, téléphones, coordonnées des enfants).
            Stockez-les dans un endroit sécurisé. Ne les partagez qu'aux personnes habilitées (trésorier, responsable pédagogique).
            Supprimez-les après leur durée d'utilité.
          </p>
        </div>
      </div>
    </div>
  );
}

function BackupCard({ icon: Icon, title, description, cta, path, params, filename, mime, testId, primary }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const r = await api.get(path, { params: params || {}, responseType: "blob" });
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      a.download = m ? m[1] : filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Téléchargement lancé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || "Erreur lors de l'export"));
    } finally {
      setBusy(false);
    }
  };
  const wrapClass = primary
    ? "bg-white border-2 border-[#1E3A8A] rounded-2xl p-5 flex flex-col sm:flex-row sm:flex-wrap items-start gap-4"
    : "bg-white border border-[#EAE6DF] rounded-2xl p-4 flex flex-col sm:flex-row items-start gap-3";
  const iconWrap = primary
    ? "w-12 h-12 rounded-xl bg-[#E0E7FF] text-[#1E3A8A] flex items-center justify-center shrink-0"
    : "w-10 h-10 rounded-xl bg-[#F4F1EB] text-[#1A2547] flex items-center justify-center shrink-0";
  return (
    <div className={wrapClass} data-testid={`backup-card-${testId}`}>
      <div className={iconWrap}><Icon className="w-5 h-5" strokeWidth={1.5} /></div>
      <div className="flex-1 min-w-0 w-full sm:w-auto">
        <h4 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">{title}</h4>
        <p className="text-xs text-[#5c6374] mt-1 leading-relaxed">{description}</p>
      </div>
      <Button
        size={primary ? "default" : "sm"}
        onClick={handle}
        disabled={busy}
        className={`rounded-full w-full sm:w-auto ${primary ? "bg-[#1E3A8A] hover:bg-[#14265E] text-white" : ""} shrink-0`}
        variant={primary ? "default" : "outline"}
        data-testid={`download-${testId}`}
      >
        <Download className="w-4 h-4 mr-1.5" />
        {busy ? "Préparation…" : cta}
      </Button>
    </div>
  );
}


function ResetSection({ title, description, endpoint, itemLabel, cta, confirmLabel, testId }) {
  const [scope, setScope] = useState("year"); // "year" | "range"
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (scope === "range" && (!start || !end)) {
      toast.error("Renseignez les deux dates");
      return;
    }
    if (scope === "range" && start > end) {
      toast.error("La date de début doit être avant la date de fin");
      return;
    }
    const desc = scope === "year"
      ? `${confirmLabel} de l'année active (EN TOTALITÉ)`
      : `${confirmLabel} entre le ${start} et le ${end}`;
    if (!window.confirm(`Êtes-vous sûr de vouloir effacer ${desc} ?\n\nCette action est irréversible.`)) return;
    setBusy(true);
    try {
      const payload = scope === "year"
        ? { scope: "year" }
        : { scope: "range", start_date: start, end_date: end };
      const { data } = await api.post(endpoint, payload);
      toast.success(`${data.deleted} ${itemLabel} supprimée(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border-2 border-[#B0413E]/40 rounded-2xl p-5" data-testid={`${testId}-section`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#FBE5E4] text-[#B0413E] flex items-center justify-center shrink-0">
          <RotateCcw className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">{title}</h4>
          <p className="text-xs text-[#5c6374] mt-1 leading-relaxed">{description}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer ${scope === "year" ? "border-[#B0413E] bg-[#FBE5E4]/40" : "border-[#EAE6DF] bg-white hover:bg-[#FCFBF8]"}`}>
          <input
            type="radio"
            name={`${testId}-scope`}
            checked={scope === "year"}
            onChange={() => setScope("year")}
            data-testid={`${testId}-scope-year`}
          />
          <span className="text-sm">Toute l'année active</span>
        </label>
        <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer ${scope === "range" ? "border-[#B0413E] bg-[#FBE5E4]/40" : "border-[#EAE6DF] bg-white hover:bg-[#FCFBF8]"}`}>
          <input
            type="radio"
            name={`${testId}-scope`}
            checked={scope === "range"}
            onChange={() => setScope("range")}
            data-testid={`${testId}-scope-range`}
          />
          <span className="text-sm">Plage de dates</span>
        </label>
      </div>

      {scope === "range" && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-[#8a968f]">Date de début</label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full rounded-xl border border-[#EAE6DF] bg-white px-3 py-2 text-sm"
              data-testid={`${testId}-start-date`}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-[#8a968f]">Date de fin</label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full rounded-xl border border-[#EAE6DF] bg-white px-3 py-2 text-sm"
              data-testid={`${testId}-end-date`}
            />
          </div>
        </div>
      )}

      <Button
        onClick={submit}
        disabled={busy}
        className="mt-4 rounded-full bg-[#B0413E] hover:bg-[#8E332F] text-white w-full sm:w-auto"
        data-testid={`${testId}-submit`}
      >
        <RotateCcw className="w-4 h-4 mr-1.5" />
        {busy ? "Suppression…" : cta}
      </Button>
    </div>
  );
}
